import { useState, useEffect } from 'react'
import {
  Radar,
  RadarChart as RechartsRadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'

const COLORS = [
  '#0e2d58','#4e6498','#96b4c9','#39774f','#a4aa35',
  '#a1c977','#f4bd48','#de6433','#9c2128','#de8da0',
  '#7c3aed','#06b6d4','#f97316','#84cc16','#ec4899',
]

export default function LLMRadarChart({ report }) {
  const [selectedModels, setSelectedModels] = useState(
    new Set(report?.llm_results?.map(llm => llm.llm_name) || [])
  )

  useEffect(() => {
    setSelectedModels(new Set(report?.llm_results?.map(llm => llm.llm_name) || []))
  }, [report])

  if (!report?.llm_results?.length) {
    return (
      <div className="text-center py-12 text-[#77726e]">
        Aucune donnée disponible
      </div>
    )
  }

  const categories = Object.keys(report.llm_results[0].categories || {})

  const data = categories.map(category => {
    const row = { category: category.charAt(0).toUpperCase() + category.slice(1) }
    report.llm_results.forEach(llm => {
      row[llm.llm_name] = llm.categories[category]?.score ?? 0
    })
    return row
  })

  const toggleModel = (name) => {
    const s = new Set(selectedModels)
    s.has(name) ? s.delete(name) : s.add(name)
    setSelectedModels(s)
  }

  const TooltipContent = ({ active, payload }) => {
    if (!active || !payload?.length) return null
    const p = payload[0].payload
    return (
      <div className="bg-white border border-[#77726e]/30 rounded-md shadow-md p-3 text-xs">
        <div className="font-bold text-[#2f3126] mb-2">{p.category}</div>
        {report.llm_results
          .filter(llm => selectedModels.has(llm.llm_name))
          .map((llm, i) => {
            const idx = report.llm_results.findIndex(x => x.llm_name === llm.llm_name)
            return (
              <div key={llm.llm_name} className="flex items-center gap-2 text-[#2f3126]">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />
                <span>{llm.llm_name} : <strong>{p[llm.llm_name]}/100</strong></span>
              </div>
            )
          })}
      </div>
    )
  }

  return (
    <div className="audit-card p-5 h-full">
      <h3 className="text-sm font-bold text-[#2f3126] uppercase tracking-wider mb-4 flex items-center gap-2">
        <span className="inline-block w-1.5 h-5 bg-[#77726e]" />
        Performance par modèle et par catégorie
      </h3>

      {/* Model toggles */}
      <div className="flex flex-wrap gap-2 mb-4">
        {report.llm_results.map((llm, index) => {
          const active = selectedModels.has(llm.llm_name)
          const color = COLORS[index % COLORS.length]
          return (
            <button
              key={llm.llm_name}
              onClick={() => toggleModel(llm.llm_name)}
              onDoubleClick={() => setSelectedModels(new Set([llm.llm_name]))}
              className="px-3 py-1 border text-xs font-semibold transition"
              style={{
                backgroundColor: active ? color : '#d1d6d8',
                borderColor: active ? color : 'rgba(119,114,110,0.4)',
                color: active ? 'white' : '#2f3126',
              }}
              title={`Double-clic pour isoler ${llm.llm_name}`}
            >
              {llm.llm_name}
            </button>
          )
        })}
        <button
          onClick={() => setSelectedModels(new Set(report.llm_results.map(l => l.llm_name)))}
          className="px-3 py-1 border border-[#4e6498]/40 text-xs text-[#4e6498] bg-[#f6f3ee] hover:bg-[#d1d6d8] transition"
        >
          Tout sélectionner
        </button>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={400}>
        <RechartsRadarChart
          data={data}
          margin={{ top: 24, right: 48, bottom: 24, left: 48 }}
        >
          <PolarGrid stroke="#e6eef2" strokeDasharray="4 4" />
          <PolarAngleAxis
            dataKey="category"
            tick={{ fontSize: 13, fontWeight: 600, fill: '#2f3126', fontFamily: 'Inria Sans, sans-serif' }}
          />
          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />

          {selectedModels.size > 0 && report.llm_results
            .filter(llm => selectedModels.has(llm.llm_name))
            .map((llm, i) => {
              const idx = report.llm_results.findIndex(x => x.llm_name === llm.llm_name)
              const color = COLORS[idx % COLORS.length]
              return (
                <Radar
                  key={llm.llm_name}
                  name={llm.llm_name}
                  dataKey={llm.llm_name}
                  stroke={color}
                  fill={color}
                  fillOpacity={i === 0 ? 0.35 : 0.15}
                  dot={{ r: i === 0 ? 4 : 3, fill: color }}
                />
              )
            })}

          <Tooltip content={<TooltipContent />} />
        </RechartsRadarChart>
      </ResponsiveContainer>
    </div>
  )
}
